import { appLogger } from '@bco/app-logger';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface RootErrorBoundaryProps {
  readonly children: ReactNode;
}

interface RootErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Last-resort top-level boundary around the whole app. Catches throws that
 * escape every page-level boundary (e.g. a failure in the router/theme/provider
 * tree) so the user sees a minimal recoverable screen instead of a permanent
 * blank root.
 *
 * Deliberately uses plain DOM with inline styles: at this level no BascProvider
 * / theme context is guaranteed to be mounted, so Basc components cannot be used
 * safely in the fallback.
 */
export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    appLogger.error('Root render error', {
      error: error.message,
      info: info.componentStack,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          minHeight: '100vh',
          padding: '1.5rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', margin: 0 }}>Something went wrong</h1>
        <p style={{ margin: 0, color: '#555', maxWidth: '32rem' }}>
          The application ran into an unexpected error. Reloading usually
          resolves it.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: '0 1.25rem',
            borderRadius: 6,
            border: '1px solid #c92a2a',
            background: '#fa5252',
            color: '#fff',
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </main>
    );
  }
}

export default RootErrorBoundary;
